/**
 * Shared FMB Outstanding broadcast constants + deep-link helpers.
 * Tenant allowlist must stay in sync with API `FMB_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS`.
 */

export const DEFAULT_FMB_OUTSTANDING_TEMPLATE_NAME = "fmb_ledger_outstanding";
export const FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY = "household_and_family";
export const FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY = "household_only";
export const DEFAULT_FMB_OUTSTANDING_MIN_PERCENT = 1;

export const FMB_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS: ReadonlySet<string> = new Set(["JP1"]);

/** Effective tenant for feature gates: impersonation wins over JWT claim. */
export function resolveEffectiveTenantId(input: {
  impersonatedTenantId?: string | null;
  jwtTenantId?: string | null;
}): string | null {
  const impersonated = (input.impersonatedTenantId || "").trim();
  if (impersonated) return impersonated;
  const jwt = (input.jwtTenantId || "").trim();
  return jwt || null;
}

/** True when FMB Outstanding mode should be shown / used for this tenant. */
export function isFmbOutstandingBroadcastEnabledForTenant(
  tenantId: string | null | undefined
): boolean {
  return FMB_OUTSTANDING_BROADCAST_ENABLED_TENANT_IDS.has((tenantId || "").trim());
}

export type FmbOutstandingBroadcastDeepLinkInput = {
  minOutstandingPercent?: number | null;
  thaliTypeId?: string | null;
  thaliTypeIds?: string[] | string | null;
  deliveryScheduleProfileId?: string | null;
  deliveryScheduleProfileIds?: string[] | string | null;
  itsIds?: string[] | string | null;
  notifyMode?: string | null;
  hijriYearStart?: number | null;
};

export type FmbOutstandingBroadcastDeepLinkDefaults = {
  broadcastKind: "FMB_OUTSTANDING";
  minOutstandingPercent: number;
  thaliTypeIds: string[];
  deliveryScheduleProfileIds: string[];
  itsIds: string[];
  notifyMode: string;
  hijriYearStart?: number;
};

function normalizeIdList(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return [...new Set(value.map((v) => String(v ?? "").trim()).filter(Boolean))];
  }
  const raw = String(value).trim();
  if (!raw) return [];
  if (raw.includes(",")) {
    return [
      ...new Set(
        raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      ),
    ];
  }
  return [raw];
}

/** Build create-route search params for FMB Outstanding with list filter criteria. */
export function buildFmbOutstandingBroadcastCreateSearch(
  input: FmbOutstandingBroadcastDeepLinkInput = {}
): string {
  const params = new URLSearchParams();
  params.set("broadcastKind", "FMB_OUTSTANDING");

  const min =
    input.minOutstandingPercent == null || Number.isNaN(Number(input.minOutstandingPercent))
      ? DEFAULT_FMB_OUTSTANDING_MIN_PERCENT
      : Number(input.minOutstandingPercent);
  params.set("minOutstandingPercent", String(min));

  const thaliTypeIds = [
    ...normalizeIdList(input.thaliTypeIds),
    ...normalizeIdList(input.thaliTypeId),
  ];
  if (thaliTypeIds.length) {
    params.set("thaliTypeIds", thaliTypeIds.join(","));
  }

  const scheduleIds = [
    ...normalizeIdList(input.deliveryScheduleProfileIds),
    ...normalizeIdList(input.deliveryScheduleProfileId),
  ];
  if (scheduleIds.length) {
    params.set("deliveryScheduleProfileIds", scheduleIds.join(","));
  }

  const itsIds = normalizeIdList(input.itsIds);
  if (itsIds.length) {
    params.set("itsIds", itsIds.join(","));
  }

  if (input.notifyMode) {
    params.set("notifyMode", String(input.notifyMode));
  }
  if (input.hijriYearStart != null && Number.isFinite(Number(input.hijriYearStart))) {
    params.set("hijriYearStart", String(Number(input.hijriYearStart)));
  }

  return params.toString();
}

export function buildFmbOutstandingBroadcastCreatePath(
  input: FmbOutstandingBroadcastDeepLinkInput = {}
): string {
  const qs = buildFmbOutstandingBroadcastCreateSearch(input);
  return qs ? `/whatsappBroadcasts/create?${qs}` : "/whatsappBroadcasts/create";
}

/** Parse create-page search params into SimpleForm defaultValues overrides. */
export function parseFmbOutstandingBroadcastDeepLink(
  searchParams: URLSearchParams | Record<string, string | null | undefined>
): Partial<FmbOutstandingBroadcastDeepLinkDefaults> | null {
  const get = (key: string): string | null => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.get(key);
    }
    const v = searchParams[key];
    return v == null || v === "" ? null : String(v);
  };

  const kind = get("broadcastKind");
  if (kind !== "FMB_OUTSTANDING") {
    return null;
  }

  const minRaw = get("minOutstandingPercent");
  const minParsed = minRaw == null ? DEFAULT_FMB_OUTSTANDING_MIN_PERCENT : Number(minRaw);
  const minOutstandingPercent =
    Number.isFinite(minParsed) && minParsed >= 0 && minParsed <= 100
      ? minParsed
      : DEFAULT_FMB_OUTSTANDING_MIN_PERCENT;

  const result: Partial<FmbOutstandingBroadcastDeepLinkDefaults> & {
    broadcastKind: "FMB_OUTSTANDING";
  } = {
    broadcastKind: "FMB_OUTSTANDING",
    minOutstandingPercent,
    thaliTypeIds: normalizeIdList(get("thaliTypeIds")),
    deliveryScheduleProfileIds: normalizeIdList(get("deliveryScheduleProfileIds")),
    itsIds: normalizeIdList(get("itsIds")),
    notifyMode: get("notifyMode") || FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  };

  const hijriRaw = get("hijriYearStart");
  if (hijriRaw != null) {
    const hijri = Number(hijriRaw);
    if (Number.isFinite(hijri) && hijri > 0) {
      result.hijriYearStart = hijri;
    }
  }

  return result;
}

/** Map FMB Data list filter values into deep-link input. */
export function fmbDataListFiltersToOutstandingDeepLink(
  filterValues: Record<string, unknown> | undefined | null
): FmbOutstandingBroadcastDeepLinkInput {
  const f = filterValues ?? {};
  return {
    minOutstandingPercent:
      f.minOutstandingPercent == null || f.minOutstandingPercent === ""
        ? DEFAULT_FMB_OUTSTANDING_MIN_PERCENT
        : Number(f.minOutstandingPercent),
    thaliTypeId: typeof f.thaliTypeId === "string" ? f.thaliTypeId : null,
    thaliTypeIds: f.thaliTypeIds as string[] | string | null | undefined,
    deliveryScheduleProfileId:
      typeof f.deliveryScheduleProfileId === "string" ? f.deliveryScheduleProfileId : null,
    deliveryScheduleProfileIds: f.deliveryScheduleProfileIds as
      | string[]
      | string
      | null
      | undefined,
  };
}
